'use client'

import { useState, useEffect } from 'react'
import { usePreferences } from '@/contexts/preferences-context'
import { BellIcon, EnvelopeIcon, DevicePhoneMobileIcon, ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline'
import { capturePostHogEvent, capturePostHogException } from '@/lib/posthog-client'
import OneSignal from 'react-onesignal'
import { Button } from '@/components/button'
import { Dialog, DialogActions, DialogBody, DialogDescription, DialogTitle } from '@/components/dialog'
import { Switch } from '@/components/switch'
import { useToast } from '@/context/ToastProvider'

const PUSH_ERROR_MESSAGE = 'Push notifications are unavailable right now. Please try again later.'
const PUSH_OPERATION_TIMEOUT_MS = 15000

function withTimeout(operation) {
  let timeoutId
  const timeout = new Promise((_, reject) => {
    timeoutId = setTimeout(() => reject(new Error('Push notification request timed out.')), PUSH_OPERATION_TIMEOUT_MS)
  })

  return Promise.race([operation, timeout]).finally(() => clearTimeout(timeoutId))
}

async function setPushDeliveryEnabled(enabled) {
  if (typeof window === 'undefined') {
    throw new Error('Browser notifications are unavailable.')
  }

  const pushSubscription = OneSignal?.User?.PushSubscription

  if (enabled) {
    const notifications = OneSignal?.Notifications
    if (!('Notification' in window) || !notifications?.requestPermission || !pushSubscription?.optIn) {
      throw new Error('OneSignal is unavailable.')
    }

    const permissionResult = notifications.permission
      || await withTimeout(Promise.resolve().then(() => notifications.requestPermission()))
    const permissionGranted = permissionResult === true || notifications.permission === true

    if (!permissionGranted) {
      throw new Error('Browser notification permission was not granted.')
    }

    await withTimeout(Promise.resolve().then(() => pushSubscription.optIn()))
    return
  }

  if (!pushSubscription?.optOut) {
    throw new Error('OneSignal is unavailable.')
  }
  await withTimeout(Promise.resolve().then(() => pushSubscription.optOut()))
}

export function AlertPreferencesDialog({ open }) {
  const { preferences, updatePreferences, loading } = usePreferences()
  const { addToast } = useToast()
  const [emailEnabled, setEmailEnabled] = useState(true)
  const [pushEnabled, setPushEnabled] = useState(false)
  const [textEnabled, setTextEnabled] = useState(false)
  const [saving, setSaving] = useState(false)
  const [permissionError, setPermissionError] = useState('')

  useEffect(() => {
    if (!open || loading) return
    setEmailEnabled(preferences.emailNotifications ?? true)
    setPushEnabled(preferences.pushNotifications ?? false)
    setTextEnabled(preferences.textNotifications ?? false)
    setPermissionError('')
  }, [loading, open, preferences.emailNotifications, preferences.pushNotifications, preferences.textNotifications])

  const togglePush = async () => {
    const nextValue = !pushEnabled
    setPermissionError('')

    if (!nextValue) {
      setPushEnabled(false)
      return
    }

    setSaving(true)
    try {
      await setPushDeliveryEnabled(true)
      setPushEnabled(true)
    } catch {
      setPushEnabled(false)
      setPermissionError(PUSH_ERROR_MESSAGE)
      addToast(PUSH_ERROR_MESSAGE, 'error')
    } finally {
      setSaving(false)
    }
  }

  const save = async () => {
    setSaving(true)
    setPermissionError('')
    try {
      if (!pushEnabled) {
        await setPushDeliveryEnabled(false).catch(() => null)
      }
      await updatePreferences({
        emailNotifications: emailEnabled,
        pushNotifications: pushEnabled,
        textNotifications: textEnabled,
        notificationSetupCompleted: true,
        notificationSetupCompletedAt: new Date().toISOString(),
      })
    } catch {
      setPermissionError(PUSH_ERROR_MESSAGE)
      addToast(PUSH_ERROR_MESSAGE, 'error')
    } finally {
      setSaving(false)
    }
  }

  const channels = [
    { id: 'email', title: 'Email alerts', description: 'Send important alert updates to your registered email.', icon: EnvelopeIcon, enabled: emailEnabled, onChange: setEmailEnabled, tone: 'bg-emerald-50 text-emerald-600' },
    { id: 'push', title: 'Browser push alerts', description: 'Show timely alerts on this browser and device.', icon: BellIcon, enabled: pushEnabled, onChange: togglePush, tone: 'bg-sky-50 text-sky-600' },
    { id: 'text', title: 'Text message alerts', description: 'Send alert updates to your registered phone number.', icon: DevicePhoneMobileIcon, enabled: textEnabled, onChange: setTextEnabled, tone: 'bg-violet-50 text-violet-600' },
  ]

  return (
    <Dialog
      open={open}
      onClose={() => {}}
      size="2xl"
      centered
      viewportClassName={preferences.sidebarCollapsed
        ? '!top-16 !right-0 !bottom-0 !left-0 !h-auto !w-auto lg:!left-20'
        : '!top-16 !right-0 !bottom-0 !left-0 !h-auto !w-auto lg:!left-72'}
    >
      <div className="border-b border-zinc-200 pb-5 dark:border-white/10">
        <div className="text-xs font-semibold uppercase tracking-[0.16em] text-sky-600 dark:text-sky-400">Alert preferences</div>
        <DialogTitle className="mt-2 !text-xl">How should we reach you?</DialogTitle>
        <DialogDescription>Choose how you want to receive alerts. You can change these options later from your account.</DialogDescription>
      </div>
      <DialogBody>
        <div className="space-y-3">
          {channels.map((channel) => {
            const Icon = channel.icon
            return (
              <div key={channel.id} className="flex items-center justify-between gap-5 rounded-xl border border-zinc-200 p-4 dark:border-white/10">
                <div className="flex items-center gap-3">
                  <span className={`flex size-10 shrink-0 items-center justify-center rounded-xl ${channel.tone}`}><Icon className="size-5" /></span>
                  <div><h3 className="text-sm font-semibold text-zinc-950 dark:text-white">{channel.title}</h3><p className="mt-1 text-sm text-zinc-500 dark:text-zinc-400">{channel.description}</p></div>
                </div>
                <Switch checked={channel.enabled} onChange={channel.onChange} disabled={saving} color="sky" aria-label={channel.title} />
              </div>
            )
          })}
        </div>
        {permissionError && <p role="alert" className="mt-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/30 dark:text-red-300">{permissionError}</p>}
      </DialogBody>
      <DialogActions><Button color="sky" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save alert preferences'}</Button></DialogActions>
    </Dialog>
  )
}

export default function UserNotifications() {
  const { preferences, updatePreference, loading } = usePreferences()
  const { addToast } = useToast()
  
  const [pushNotifications, setPushNotifications] = useState(false)
  const [emailNotifications, setEmailNotifications] = useState(false)
  const [textNotifications, setTextNotifications] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [showDebug, setShowDebug] = useState(false)
  const [debugInfo, setDebugInfo] = useState(null)
  const [pushPermissionError, setPushPermissionError] = useState('')

  // Load preferences when available
  useEffect(() => {
    if (!loading) {
      setPushNotifications(preferences.pushNotifications ?? false)
      setEmailNotifications(preferences.emailNotifications ?? true)
      setTextNotifications(preferences.textNotifications ?? false)
    }
  }, [loading, preferences])

  // Fetch OneSignal debug info
  const fetchDebugInfo = async () => {
    try {
      const info = {
        timestamp: new Date().toISOString(),
        oneSignalAvailable: typeof window !== 'undefined' && !!window.OneSignal,
        playerId: null,
        subscriptionState: null,
        permissionState: null,
        userTags: null,
        deviceInfo: null,
        error: null
      }

      if (typeof window !== 'undefined' && window.OneSignal) {
        try {
          // Get Player ID - try both old and new API
          try {
            if (window.OneSignal.User?.PushSubscription?.id) {
              info.playerId = window.OneSignal.User.PushSubscription.id
            } else if (typeof window.OneSignal.getUserId === 'function') {
              info.playerId = await window.OneSignal.getUserId()
            }
          } catch (err) {
            info.playerId = `Error: ${err.message}`
          }

          // Get subscription state
          try {
            if (window.OneSignal.User?.PushSubscription) {
              info.subscriptionState = {
                enabled: !!window.OneSignal.User.PushSubscription.id,
                optedIn: window.OneSignal.User.PushSubscription.optedIn,
                token: window.OneSignal.User.PushSubscription.token ? 'Present' : 'None'
              }
            } else if (typeof window.OneSignal.isPushNotificationsEnabled === 'function') {
              const isPushEnabled = await window.OneSignal.isPushNotificationsEnabled()
              info.subscriptionState = {
                enabled: isPushEnabled,
                supported: typeof window.OneSignal.isPushNotificationsSupported === 'function' 
                  ? window.OneSignal.isPushNotificationsSupported() 
                  : 'Unknown'
              }
            }
          } catch (err) {
            info.subscriptionState = { error: err.message }
          }

          // Get permission state
          try {
            if (window.OneSignal.Notifications?.permission) {
              info.permissionState = window.OneSignal.Notifications.permission
            } else if (typeof window.OneSignal.getNotificationPermission === 'function') {
              info.permissionState = await window.OneSignal.getNotificationPermission()
            } else if (Notification?.permission) {
              info.permissionState = Notification.permission
            }
          } catch (err) {
            info.permissionState = `Error: ${err.message}`
          }

          // Get user tags
          try {
            if (typeof window.OneSignal.getTags === 'function') {
              info.userTags = await window.OneSignal.getTags()
            } else if (window.OneSignal.User?.getTags) {
              info.userTags = window.OneSignal.User.getTags()
            }
          } catch (err) {
            info.userTags = { error: err.message }
          }

          // Get device info
          info.deviceInfo = {
            userAgent: navigator.userAgent,
            platform: navigator.platform,
            language: navigator.language
          }
        } catch (err) {
          info.error = err.message
        }
      }

      setDebugInfo(info)
    } catch (error) {
      setDebugInfo({ error: error.message })
    }
  }

  useEffect(() => {
    if (showDebug) {
      fetchDebugInfo()
    }
  }, [showDebug])

  const handleToggle = async (type, value) => {
    setIsSaving(true)
    if (type === 'pushNotifications') setPushPermissionError('')
    try {
      if (type === 'pushNotifications') {
        await setPushDeliveryEnabled(value)
      }

      await updatePreference(type, value)
      
      // Update local state
      switch(type) {
        case 'pushNotifications':
          setPushNotifications(value)
          break
        case 'emailNotifications':
          setEmailNotifications(value)
          break
        case 'textNotifications':
          setTextNotifications(value)
          break
      }
      capturePostHogEvent('notification_preference_updated', {
        notification_channel: type,
        enabled: value,
      })
    } catch (error) {
      if (type === 'pushNotifications') {
        setPushPermissionError(PUSH_ERROR_MESSAGE)
        addToast(PUSH_ERROR_MESSAGE, 'error')
      }
      capturePostHogException(error, { flow: 'notification_preference_update', notification_channel: type })
      console.error('Error updating notification preference:', error)
    } finally {
      setIsSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="p-6 bg-white">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="h-4 bg-gray-200 rounded w-2/3 mb-8"></div>
          <div className="space-y-6">
            <div className="h-16 bg-gray-200 rounded"></div>
            <div className="h-16 bg-gray-200 rounded"></div>
            <div className="h-16 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="p-6 bg-white">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Notification Preferences</h2>
        <p className="mt-1 text-sm text-gray-600">
          Choose how you want to receive notifications from Hazcams
        </p>
      </div>

      <div className="space-y-6">
        {/* Push Notifications */}
        <div className="bg-white border border-gray-200 rounded-lg p-6 hover:border-gray-300 transition-colors">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <BellIcon className="w-6 h-6 text-blue-600" />
                </div>
              </div>
              <div className="flex-1">
                <h3 className="text-base font-semibold text-gray-900">Push Notifications</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Receive real-time browser notifications
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => handleToggle('pushNotifications', !pushNotifications)}
              disabled={isSaving}
              className={`
                relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent 
                transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2
                ${pushNotifications ? 'bg-blue-600' : 'bg-gray-200'}
                ${isSaving ? 'opacity-50 cursor-not-allowed' : ''}
              `}
              role="switch"
              aria-checked={pushNotifications}
            >
              <span
                aria-hidden="true"
                className={`
                  pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 
                  transition duration-200 ease-in-out
                  ${pushNotifications ? 'translate-x-5' : 'translate-x-0'}
                `}
              />
            </button>
          </div>
          {pushPermissionError && (
            <p role="alert" className="mt-3 text-sm text-red-600">
              {pushPermissionError}
            </p>
          )}
        </div>

        {/* Email Notifications */}
        <div className="bg-white border border-gray-200 rounded-lg p-6 hover:border-gray-300 transition-colors">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <EnvelopeIcon className="w-6 h-6 text-green-600" />
                </div>
              </div>
              <div className="flex-1">
                <h3 className="text-base font-semibold text-gray-900">Email Notifications</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Emails will be sent to your registered email address.
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => handleToggle('emailNotifications', !emailNotifications)}
              disabled={isSaving}
              className={`
                relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent 
                transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-green-600 focus:ring-offset-2
                ${emailNotifications ? 'bg-green-600' : 'bg-gray-200'}
                ${isSaving ? 'opacity-50 cursor-not-allowed' : ''}
              `}
              role="switch"
              aria-checked={emailNotifications}
            >
              <span
                aria-hidden="true"
                className={`
                  pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 
                  transition duration-200 ease-in-out
                  ${emailNotifications ? 'translate-x-5' : 'translate-x-0'}
                `}
              />
            </button>
          </div>
        </div>

        {/* Text Notifications */}
        <div className="bg-white border border-gray-200 rounded-lg p-6 hover:border-gray-300 transition-colors">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <DevicePhoneMobileIcon className="w-6 h-6 text-purple-600" />
                </div>
              </div>
              <div className="flex-1">
                <h3 className="text-base font-semibold text-gray-900">Text Notifications</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Be sure to configure your phone number in your account settings to receive text alerts
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => handleToggle('textNotifications', !textNotifications)}
              disabled={isSaving}
              className={`
                relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent 
                transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-purple-600 focus:ring-offset-2
                ${textNotifications ? 'bg-purple-600' : 'bg-gray-200'}
                ${isSaving ? 'opacity-50 cursor-not-allowed' : ''}
              `}
              role="switch"
              aria-checked={textNotifications}
            >
              <span
                aria-hidden="true"
                className={`
                  pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 
                  transition duration-200 ease-in-out
                  ${textNotifications ? 'translate-x-5' : 'translate-x-0'}
                `}
              />
            </button>
          </div>
        </div>
      </div>

      <div className="mt-8 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <p className="text-sm text-blue-800">
          <strong>Note:</strong> Your notification preferences are saved automatically. 
          Changes take effect immediately.
        </p>
      </div>

      {/* Debug Section */}
      <div className="mt-6 border border-gray-300 rounded-lg overflow-hidden">
        <button
          onClick={() => setShowDebug(!showDebug)}
          className="w-full px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors flex items-center justify-between text-left"
        >
          <span className="text-sm font-semibold text-gray-700">
            Debug Information
          </span>
          {showDebug ? (
            <ChevronUpIcon className="w-5 h-5 text-gray-500" />
          ) : (
            <ChevronDownIcon className="w-5 h-5 text-gray-500" />
          )}
        </button>
        
        {showDebug && (
          <div className="p-4 bg-gray-50 border-t border-gray-200">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-semibold text-gray-900">Notification Debug Info</h4>
              <button
                onClick={fetchDebugInfo}
                className="text-xs px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded transition-colors"
              >
                Refresh
              </button>
            </div>
            
            {debugInfo ? (
              <div className="space-y-3">
                {debugInfo.error ? (
                  <div className="p-3 bg-red-50 border border-red-200 rounded">
                    <p className="text-sm text-red-700">Error: {debugInfo.error}</p>
                  </div>
                ) : (
                  <>
                    <div className="grid grid-cols-2 gap-3 text-xs">
                      <div className="p-2 bg-white border border-gray-200 rounded">
                        <div className="text-gray-600 mb-1">Notifications Available</div>
                        <div className="font-mono text-green-600 font-semibold">
                          {debugInfo.oneSignalAvailable ? '✓ Yes' : '✗ No'}
                        </div>
                      </div>
                      
                      <div className="p-2 bg-white border border-gray-200 rounded">
                        <div className="text-gray-600 mb-1">Permission State</div>
                        <div className="font-mono text-blue-600 font-semibold">
                          {debugInfo.permissionState || 'N/A'}
                        </div>
                      </div>
                    </div>

                    <div className="p-2 bg-white border border-gray-200 rounded">
                      <div className="text-gray-600 text-xs mb-1">ID</div>
                      <div className="font-mono text-xs text-gray-900 break-all">
                        {debugInfo.playerId || 'Not available'}
                      </div>
                    </div>

                    {debugInfo.subscriptionState && (
                      <div className="p-2 bg-white border border-gray-200 rounded">
                        <div className="text-gray-600 text-xs mb-1">Subscription State</div>
                        <div className="font-mono text-xs space-y-1">
                          {debugInfo.subscriptionState.error ? (
                            <div className="text-red-600">Error: {debugInfo.subscriptionState.error}</div>
                          ) : (
                            <>
                              {debugInfo.subscriptionState.enabled !== undefined && (
                                <div>Enabled: <span className={debugInfo.subscriptionState.enabled ? 'text-green-600 font-semibold' : 'text-red-600 font-semibold'}>
                                  {debugInfo.subscriptionState.enabled ? '✓ Yes' : '✗ No'}
                                </span></div>
                              )}
                              {debugInfo.subscriptionState.optedIn !== undefined && (
                                <div>Ext Opt In: <span className={debugInfo.subscriptionState.optedIn ? 'text-green-600 font-semibold' : 'text-red-600 font-semibold'}>
                                  {debugInfo.subscriptionState.optedIn ? '✓ Yes' : '✗ No'}
                                </span></div>
                              )}
                              {debugInfo.subscriptionState.token !== undefined && (
                                <div>Token: <span className="text-blue-600 font-semibold">{debugInfo.subscriptionState.token}</span></div>
                              )}
                              {debugInfo.subscriptionState.supported !== undefined && (
                                <div>Supported: <span className={debugInfo.subscriptionState.supported ? 'text-green-600 font-semibold' : 'text-red-600 font-semibold'}>
                                  {debugInfo.subscriptionState.supported ? '✓ Yes' : '✗ No'}
                                </span></div>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    )}

                    {debugInfo.userTags && Object.keys(debugInfo.userTags).length > 0 && (
                      <div className="p-2 bg-white border border-gray-200 rounded">
                        <div className="text-gray-600 text-xs mb-1">User Tags</div>
                        <div className="font-mono text-xs">
                          <pre className="text-purple-600 overflow-x-auto">
                            {JSON.stringify(debugInfo.userTags, null, 2)}
                          </pre>
                        </div>
                      </div>
                    )}

                    {debugInfo.deviceInfo && (
                      <div className="p-2 bg-white border border-gray-200 rounded">
                        <div className="text-gray-600 text-xs mb-1">Device Info</div>
                        <div className="font-mono text-xs space-y-1 text-gray-900">
                          <div>Platform: <span className="text-gray-700">{debugInfo.deviceInfo.platform}</span></div>
                          <div>Language: <span className="text-gray-700">{debugInfo.deviceInfo.language}</span></div>
                          <div className="text-gray-600 text-[10px] break-all mt-2">
                            UA: {debugInfo.deviceInfo.userAgent}
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="text-xs text-gray-500 pt-2 border-t border-gray-200">
                      Last updated: {new Date(debugInfo.timestamp).toLocaleString()}
                    </div>
                  </>
                )}
              </div>
            ) : (
              <div className="text-sm text-gray-600 text-center py-4">
                Loading debug information...
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
